import React, { useState, useEffect } from 'react';
import { userService } from '@/services/userService';
import { X, Plus } from 'lucide-react';

const PostScryptContacts = ({ onClose }) => {
  const [executors, setExecutors] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newExecutor, setNewExecutor] = useState({ name: '', email: '', confirmEmail: '' });
  const [showNotification, setShowNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingExecutors, setFetchingExecutors] = useState(false);
  const [emailMismatch, setEmailMismatch] = useState(false);

  useEffect(() => {
    fetchExecutors();
  }, []);

  const fetchExecutors = async () => {
    setFetchingExecutors(true);
    setError(null);
    
    try {
      const response = await userService.getMyExecutors();
      setExecutors(response);
    } catch (err) {
      setError('Failed to load contacts. Please try again.');
      console.error('Error fetching contacts:', err);
      setExecutors([]);
    } finally {
      setFetchingExecutors(false);
    }
  };

  const handleAddExecutor = async (e) => {
    e.preventDefault();
    if (newExecutor.email !== newExecutor.confirmEmail) {
      setEmailMismatch(true);
      return;
    }
    setEmailMismatch(false);
    
    if (newExecutor.name && newExecutor.email) {
      setIsLoading(true);
      setError(null);
      
      try {
        await userService.addExecutor({ name: newExecutor.name, email: newExecutor.email });
        await fetchExecutors();
        setNewExecutor({ name: '', email: '', confirmEmail: '' });
        setShowAddForm(false);
        setShowNotification(true);
      } catch (err) {
        setError('Failed to add contact. Please try again.');
        console.error('Error adding contact:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEmailChange = (e) => {
    setEmailMismatch(false);
    setNewExecutor({ ...newExecutor, email: e.target.value });
  };

  const handleConfirmEmailChange = (e) => {
    setEmailMismatch(false);
    setNewExecutor({ ...newExecutor, confirmEmail: e.target.value });
  };

  return (
    <div className="space-y-6">
      {showNotification && (
        <div className="mb-6 p-4 bg-[#F16745] text-white rounded-lg relative">
          <button
            onClick={() => setShowNotification(false)}
            className="absolute top-4 right-4 text-white hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <p className="pr-6">
            Your new PostScrypt contact will receive an email with all the details. If they have not received the email, please contact us directly.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-500 text-white rounded-lg relative">
          <button
            onClick={() => setError(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
          <p className="pr-6">{error}</p>
        </div>
      )}

      {fetchingExecutors ? (
        <div className="py-8 text-center text-gray-500">
          Loading PostScrypt contacts...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {executors.length > 0 ? (
            executors.map((executor, index) => (
              <div
                key={index}
                className="p-4 bg-gray-50 rounded-lg flex flex-col gap-1"
              >
                <div className="font-medium text-gray-900 break-words">{executor.name}</div>
                <div className="text-gray-500 text-sm break-all">{executor.email}</div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-4 text-gray-500">
              No PostScrypt contacts found. Add your first PostScrypt contact.
            </div>
          )}
        </div>
      )}

      {!showAddForm ? (
        <button
          onClick={() => setShowAddForm(true)}
          className="mt-6 w-full flex items-center justify-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-[#F16745] hover:bg-[#F16745] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745]"
        >
          <Plus className="h-5 w-5 mr-2" /> Add New PostScrypt Contact
        </button>
      ) : (
        <form onSubmit={handleAddExecutor} className="mt-6">
          <div className="bg-gray-50 p-6 rounded-lg space-y-6">
            <div>
              <label htmlFor="name" className="block font-semibold text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  value={newExecutor.name}
                  onChange={(e) => setNewExecutor({ ...newExecutor, name: e.target.value })}
                  className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] transition-colors bg-white"
                  placeholder="Enter contact full name"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  value={newExecutor.email}
                  onChange={handleEmailChange}
                  className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] transition-colors bg-white"
                  placeholder="Enter contact email"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="confirmEmail" className="block font-semibold text-gray-700 mb-2">
                Confirm Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="confirmEmail"
                  value={newExecutor.confirmEmail}
                  onChange={handleConfirmEmailChange}
                  className={`block w-full px-4 py-3 rounded-lg border ${emailMismatch ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] transition-colors bg-white`}
                  placeholder="Confirm contact email"
                  required
                />
                {emailMismatch && (
                  <p className="mt-2 text-sm text-red-600">Email addresses do not match</p>
                )}
              </div>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="submit"
              disabled={isLoading}
              className={`flex-1 px-4 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-[#F16745] hover:bg-[#F16745] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? 'Adding...' : 'Add PostScrypt Contact'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setEmailMismatch(false);
                setNewExecutor({ name: '', email: '', confirmEmail: '' });
              }}
              disabled={isLoading}
              className="flex-1 px-4 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PostScryptContacts; 